import { recoveryPoolInnerCall } from "./sdk";
import { ethers } from "ethers";
import type { JoinSplitNote } from "../joinsplit/types";
import { BN254_SCALAR_FIELD } from "./notes";
import type { FlatJoinSplitV2JournalRecord } from "./pendingJournal";
import { PRIVATE_DEPOSIT_MERGE_ABI } from "./privateFeeDepositMergeAbi";

export const DEPOSIT_EVIDENCE_INTERFACE = new ethers.Interface([
  ...PRIVATE_DEPOSIT_MERGE_ABI,
  "function depositTokenWithPrivateFees(address token,(bytes32 tokenHash,bytes32[] commitments,uint256[] amounts,bytes[] proofs) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes signature)",
  "function depositWithPrivateFees((bytes32 tokenHash,bytes32[] commitments,uint256[] amounts,bytes[] proofs) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes signature) payable",
  "function depositEth(bytes32 commitment,bytes32 tokenHash,bytes proof) payable",
  "function deposit(bytes32 commitment,address token,uint256 amount,bytes32 tokenHash,bytes proof)",
  "function depositAndMerge((bytes32 merkleRoot,bytes32 nullifier,bytes32 commitment,address token,uint256 amount,uint64 deadline,bytes32 nonce,bytes proof) data) payable",
  "function computeTokenHashForTree(uint256 treeIndex,address token,uint8 tokenType,uint256 tokenSubID) view returns (bytes32)",
  "event Deposit(bytes32 indexed commitment,uint256 indexed treeIndex,uint256 leafIndex,address token,uint256 amount)",
  "event DepositMerged(uint256 indexed treeIndex,bytes32 indexed nullifier,bytes32 commitment,uint256 leafIndex,address token,uint256 netCredit)",
  "event LeafInsertedInTree(uint256 indexed treeIndex,bytes32 leaf,uint256 leafIndex)",
]);

function fail(message: string): never {
  throw new Error(`Deposit confirmation pending: ${message}`);
}

/** Public merge fields recovered from a durable deposit journal. */
export interface DepositMergeJournalEvidence {
  sourceNullifier: string;
  netCredit: bigint;
  deadline: bigint;
}

/** Return merge-only public evidence, or null for an ordinary deposit. */
export function depositMergeEvidenceFromJournal(
  record: FlatJoinSplitV2JournalRecord,
): DepositMergeJournalEvidence | null {
  if (record.kind !== "deposit" || record.outputNotes.length !== 1) return null;
  const parsed = DEPOSIT_EVIDENCE_INTERFACE.parseTransaction({
    data: recoveryPoolInnerCall(record.transaction.data),
  });
  if (
    !parsed ||
    ![
      "depositAndMerge",
      "depositAndMergeWithPrivateFees",
      "depositTokenAndMergeWithPrivateFees",
    ].includes(parsed.name)
  )
    return null;
  const data =
    parsed.name === "depositAndMerge"
      ? parsed.args.data
      : parsed.args.data.merge;
  if (
    record.sourceNullifiers.length !== 1 ||
    record.sourceCommitments.length !== 1 ||
    String(data.nullifier).toLowerCase() !== record.sourceNullifiers[0]
  ) {
    fail("invalid deposit merge journal source");
  }
  return {
    sourceNullifier: record.sourceNullifiers[0],
    netCredit: BigInt(data.amount),
    deadline: BigInt(data.deadline),
  };
}

/** Bind a restored journal's intent to its single encrypted deposit note. */
export function depositNoteFromJournal(
  record: FlatJoinSplitV2JournalRecord,
): JoinSplitNote {
  if (record.kind !== "deposit" || record.outputNotes.length !== 1) {
    fail("invalid deposit journal shape");
  }
  const note = record.outputNotes[0];
  const intent = {
    ...record.transaction,
    data: recoveryPoolInnerCall(record.transaction.data),
  };
  const parsed = DEPOSIT_EVIDENCE_INTERFACE.parseTransaction({
    data: intent.data,
  });
  if (
    !parsed ||
    ![
      "deposit",
      "depositEth",
      "depositWithPrivateFees",
      "depositTokenWithPrivateFees",
      "depositAndMerge",
      "depositAndMergeWithPrivateFees",
      "depositTokenAndMergeWithPrivateFees",
    ].includes(parsed.name) ||
    DEPOSIT_EVIDENCE_INTERFACE.encodeFunctionData(
      parsed.fragment,
      parsed.args,
    ).toLowerCase() !== intent.data.toLowerCase() ||
    ethers.getAddress(intent.to) !== ethers.getAddress(record.poolAddress) ||
    BigInt(intent.chainId) !== BigInt(record.chainId)
  ) {
    fail("journal does not contain a canonical deposit intent");
  }

  if (
    [
      "depositAndMerge",
      "depositAndMergeWithPrivateFees",
      "depositTokenAndMergeWithPrivateFees",
    ].includes(parsed.name)
  ) {
    const feeBearing = parsed.name !== "depositAndMerge";
    const data = feeBearing ? parsed.args.data.merge : parsed.args.data;
    const token = ethers.getAddress(data.token);
    const feeData = feeBearing ? parsed.args.data : null;
    const fees = feeData
      ? Array.from(feeData.feeAmounts, (value) => BigInt(String(value)))
      : [];
    const grossAmount =
      BigInt(data.amount) + fees.reduce((sum, value) => sum + value, 0n);
    if (
      feeData &&
      (feeData.feeCommitments.length > 2 ||
        feeData.feeCommitments.length !== fees.length ||
        feeData.feeProofs.length !== fees.length ||
        fees.some((value) => value <= 0n || value >= 1n << 120n) ||
        !feeData.feeProofs.every(
          (proof: string) => ethers.dataLength(proof) === 256,
        ) ||
        BigInt(feeData.tokenHash) !== note.tokenHash ||
        (parsed.name === "depositAndMergeWithPrivateFees" &&
          token !== ethers.ZeroAddress) ||
        (parsed.name === "depositTokenAndMergeWithPrivateFees" &&
          (token === ethers.ZeroAddress ||
            ethers.getAddress(parsed.args.token) !== token ||
            BigInt(parsed.args.grossAmount) !== grossAmount)))
    ) {
      fail("private-fee merge funding does not match its saved note");
    }
    if (
      record.sourceNullifiers.length !== 1 ||
      record.sourceCommitments.length !== 1 ||
      String(data.nullifier).toLowerCase() !== record.sourceNullifiers[0] ||
      String(data.commitment).toLowerCase() !== note.commitment.toLowerCase() ||
      !ethers.isHexString(data.merkleRoot, 32) ||
      BigInt(data.merkleRoot) >= BN254_SCALAR_FIELD ||
      !ethers.isHexString(data.nullifier, 32) ||
      BigInt(data.nullifier) <= 0n ||
      BigInt(data.nullifier) >= BN254_SCALAR_FIELD ||
      !ethers.isHexString(data.nonce, 32) ||
      BigInt(data.nonce) === 0n ||
      BigInt(data.deadline) <= 0n ||
      BigInt(data.amount) <= 0n ||
      BigInt(data.amount) >= 1n << 120n ||
      !note.tokenData ||
      ethers.getAddress(note.tokenData.tokenAddress) !== token ||
      (token === ethers.ZeroAddress
        ? BigInt(intent.value) !== grossAmount
        : BigInt(intent.value) !== 0n) ||
      ethers.dataLength(data.proof) !== 256
    ) {
      fail("deposit merge intent does not match its saved replacement note");
    }
    return note;
  }

  if (
    record.sourceNullifiers.length !== 0 ||
    record.sourceCommitments.length !== 0
  ) {
    fail("ordinary deposit journal unexpectedly spends private inputs");
  }
  if (
    parsed.name === "depositWithPrivateFees" ||
    parsed.name === "depositTokenWithPrivateFees"
  ) {
    const d = parsed.args.data;
    const token =
      parsed.name === "depositWithPrivateFees"
        ? ethers.ZeroAddress
        : ethers.getAddress(parsed.args.token);
    if (
      d.commitments.length < 1 ||
      d.commitments.length > 3 ||
      d.commitments.length !== d.amounts.length ||
      d.proofs.length !== d.commitments.length ||
      d.commitments[0].toLowerCase() !== note.commitment.toLowerCase() ||
      d.amounts[0] !== note.value ||
      BigInt(d.tokenHash) !== note.tokenHash ||
      ethers.getAddress(note.tokenData.tokenAddress) !== token ||
      (parsed.name === "depositTokenWithPrivateFees" &&
        token === ethers.ZeroAddress) ||
      (token === ethers.ZeroAddress
        ? d.amounts.reduce((sum: bigint, amount: bigint) => sum + amount, 0n)
        : 0n) !== BigInt(intent.value) ||
      !d.proofs.every((p: string) => ethers.dataLength(p) === 256)
    )
      fail("private-fee deposit does not match its saved note");
    return note;
  }
  const isEth = parsed.name === "depositEth";
  const token = isEth
    ? ethers.ZeroAddress
    : ethers.getAddress(parsed.args.token);
  if (
    (!isEth && token === ethers.ZeroAddress) ||
    String(parsed.args.commitment).toLowerCase() !==
      note.commitment.toLowerCase() ||
    BigInt(parsed.args.tokenHash) !== note.tokenHash ||
    ethers.getAddress(note.tokenData.tokenAddress) !== token ||
    BigInt(isEth ? intent.value : parsed.args.amount) !== note.value ||
    (!isEth && BigInt(intent.value) !== 0n) ||
    ethers.dataLength(parsed.args.proof) !== 256
  ) {
    fail("journal deposit intent does not match its saved note");
  }
  return note;
}

/**
 * The pool's authenticated outcome, not a wallet-specific outer envelope, is
 * deposit authority. A wallet may batch the call or a third party may copy it.
 * Neither can substitute the note's commitment, value or tree/token domain.
 * Only a canonical successful receipt with BOTH matching pool events qualifies.
 * This rule is deposit-only; it must not authorize arbitrary spend/bridge calls.
 */
export async function verifyFreshPoolDepositReceipt(params: {
  provider: ethers.Provider;
  receipt: ethers.TransactionReceipt;
  chainId: number | bigint;
  pool: string;
  note: Pick<
    JoinSplitNote,
    "commitment" | "tokenData" | "tokenHash" | "value" | "treeIndex"
  >;
}): Promise<{ treeIndex: number; leafIndex: number }> {
  const { provider, receipt, note } = params;
  const pool = ethers.getAddress(params.pool);
  if (
    !ethers.isHexString(note.commitment, 32) ||
    BigInt(note.commitment) <= 0n ||
    BigInt(note.commitment) >= BN254_SCALAR_FIELD ||
    note.tokenHash <= 0n ||
    note.tokenHash >= BN254_SCALAR_FIELD ||
    note.value <= 0n ||
    note.value >= 1n << 120n ||
    note.treeIndex === undefined ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex < 0 ||
    note.tokenData.tokenType !== 0 ||
    BigInt(note.tokenData.tokenSubID || "0") !== 0n
  ) {
    fail("invalid deposit note metadata");
  }
  if (
    !receipt ||
    receipt.status !== 1 ||
    !ethers.isHexString(receipt.hash, 32) ||
    !ethers.isHexString(receipt.blockHash, 32) ||
    !Number.isSafeInteger(receipt.blockNumber) ||
    receipt.blockNumber < 0
  ) {
    fail("successful deposit receipt is unavailable");
  }
  const [network, block, transaction] = await Promise.all([
    provider.getNetwork(),
    provider.getBlock(receipt.blockNumber),
    provider.getTransaction(receipt.hash),
  ]);
  if (
    BigInt(network.chainId) !== BigInt(params.chainId) ||
    !transaction ||
    transaction.chainId == null ||
    BigInt(transaction.chainId) !== BigInt(params.chainId) ||
    transaction.hash.toLowerCase() !== receipt.hash.toLowerCase() ||
    transaction.blockNumber !== receipt.blockNumber ||
    transaction.blockHash?.toLowerCase() !== receipt.blockHash.toLowerCase() ||
    transaction.to?.toLowerCase() !== receipt.to?.toLowerCase() ||
    transaction.from.toLowerCase() !== receipt.from.toLowerCase()
  ) {
    fail("receipt transaction or chain is inconsistent");
  }
  if (
    block?.number !== receipt.blockNumber ||
    block.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()
  ) {
    fail("deposit receipt is no longer canonical");
  }
  const events = (name: "Deposit" | "LeafInsertedInTree") => {
    const fragment = DEPOSIT_EVIDENCE_INTERFACE.getEvent(name)!;
    return receipt.logs
      .filter(
        (log) =>
          log.address.toLowerCase() === pool.toLowerCase() &&
          log.topics[0]?.toLowerCase() === fragment.topicHash.toLowerCase(),
      )
      .map((log) => {
        if (
          log.removed ||
          log.blockNumber !== receipt.blockNumber ||
          log.blockHash.toLowerCase() !== receipt.blockHash.toLowerCase() ||
          log.transactionHash.toLowerCase() !== receipt.hash.toLowerCase()
        ) {
          fail("pool event is not bound to the canonical receipt");
        }
        const decoded = DEPOSIT_EVIDENCE_INTERFACE.decodeEventLog(
          fragment,
          log.data,
          [...log.topics],
        );
        const encoded = DEPOSIT_EVIDENCE_INTERFACE.encodeEventLog(
          fragment,
          decoded,
        );
        if (
          encoded.data.toLowerCase() !== log.data.toLowerCase() ||
          encoded.topics.length !== log.topics.length ||
          encoded.topics.some(
            (topic, i) => topic.toLowerCase() !== log.topics[i].toLowerCase(),
          )
        ) {
          fail("pool event has noncanonical encoding");
        }
        return decoded;
      })
      .filter(
        (event) =>
          String(
            name === "Deposit" ? event.commitment : event.leaf,
          ).toLowerCase() === note.commitment.toLowerCase(),
      );
  };
  const deposits = events("Deposit"),
    leaves = events("LeafInsertedInTree");
  if (deposits.length !== 1 || leaves.length !== 1) {
    fail("receipt must contain one matching Deposit and leaf insertion");
  }
  const deposit = deposits[0],
    leaf = leaves[0];
  if (
    ethers.getAddress(deposit.token) !==
      ethers.getAddress(note.tokenData.tokenAddress) ||
    BigInt(deposit.amount) !== note.value ||
    BigInt(deposit.treeIndex) !== BigInt(note.treeIndex!) ||
    BigInt(leaf.treeIndex) !== BigInt(deposit.treeIndex) ||
    BigInt(leaf.leafIndex) !== BigInt(deposit.leafIndex) ||
    BigInt(deposit.leafIndex) < 0n ||
    BigInt(deposit.leafIndex) >= 1n << 24n
  ) {
    fail("pool events do not match the saved deposit note");
  }
  const rawDomain = await provider.call({
    to: pool,
    blockTag: receipt.blockNumber,
    data: DEPOSIT_EVIDENCE_INTERFACE.encodeFunctionData(
      "computeTokenHashForTree",
      [note.treeIndex, note.tokenData.tokenAddress, 0, 0],
    ),
  });
  if (
    !ethers.isHexString(rawDomain, 32) ||
    BigInt(rawDomain) !== note.tokenHash
  ) {
    fail("receipt tree/token domain does not match the saved note");
  }
  const after = await provider.getBlock(receipt.blockNumber);
  if (
    after?.number !== receipt.blockNumber ||
    after.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()
  ) {
    fail("deposit receipt changed during verification");
  }
  return { treeIndex: note.treeIndex!, leafIndex: Number(deposit.leafIndex) };
}
